<template>
  <div class="trf-page">
    <container>
      <div class="pg-title-row">
        <div>
          <h1 class="pg-title">Add Trainer</h1>
          <p class="pg-title-sub">Create a trainer account, profile details and mobile app introduction</p>
        </div>
      </div>

      <div class="trf-card">
        <div class="trf-card-header">
          <span class="trf-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 10L12 5 2 10l10 5 10-5z"/>
              <path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/>
            </svg>
          </span>
          <div>
            <h2 class="trf-card-title">Trainer Profile</h2>
            <p class="trf-card-subtitle">Set up the login identity, public profile and trainer media</p>
          </div>
        </div>

        <div class="trf-error" v-if="error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          {{ error }}
        </div>

        <v-form :handle-submit="handleSubmit">
          <section class="trf-section">
            <div class="trf-section-heading">
              <span class="trf-section-kicker">Account Identity</span>
              <p class="trf-section-copy">These fields create the trainer login and display name.</p>
            </div>

            <div class="trf-field-grid">
              <v-text-field
                class="trf-field"
                name="firstName"
                @keyup.enter="handleSubmit"
                v-model="trainer.firstName"
                v-validate="'required'" data-vv-value-path="input" data-vv-name="firstName"
                :message-error="errors && errors.first ? errors.first('firstName') : ''"
                :has-error="errors && errors.has ? errors.has('firstName') : ''"
                :block="true">
                <template slot="label">
                  First name <span class="trf-required">*</span>
                </template>
              </v-text-field>

              <v-text-field
                class="trf-field"
                name="lastName"
                @keyup.enter="handleSubmit"
                v-model="trainer.lastName"
                v-validate="'required'" data-vv-value-path="input" data-vv-name="lastName"
                :message-error="errors && errors.first ? errors.first('lastName') : ''"
                :has-error="errors && errors.has ? errors.has('lastName') : ''"
                :block="true">
                <template slot="label">
                  Last name <span class="trf-required">*</span>
                </template>
              </v-text-field>

              <v-text-field
                class="trf-field"
                name="user.email"
                @keyup.enter="handleSubmit"
                v-model="trainer.user.email"
                v-validate="'required|email'" data-vv-value-path="input" data-vv-name="user.email"
                :message-error="errors && errors.first ? errors.first('user.email') : ''"
                :has-error="errors && errors.has ? errors.has('user.email') : ''"
                :block="true">
                <template slot="label">
                  E-mail <span class="trf-required">*</span>
                </template>
              </v-text-field>

              <v-text-field
                class="trf-field"
                name="nickname"
                @keyup.enter="handleSubmit"
                v-model="trainer.nickname"
                v-validate="'required'" data-vv-value-path="input" data-vv-name="nickname"
                :message-error="errors && errors.first ? errors.first('nickname') : ''"
                :has-error="errors && errors.has ? errors.has('nickname') : ''"
                :block="true">
                <template slot="label">
                  Nickname (username) <span class="trf-required">*</span>
                </template>
              </v-text-field>

              <v-text-field
                class="trf-field"
                name="user.password"
                @keyup.enter="handleSubmit"
                type="password"
                v-model="trainer.user.password"
                v-validate="'required'" data-vv-value-path="input" data-vv-name="user.password"
                :message-error="errors && errors.first ? errors.first('user.password') : ''"
                :has-error="errors && errors.has ? errors.has('user.password') : ''"
                :block="true">
                <template slot="label">
                  Password <span class="trf-required">*</span>
                </template>
              </v-text-field>
            </div>
          </section>

          <section class="trf-section">
            <div class="trf-section-heading">
              <span class="trf-section-kicker">Profile Details</span>
              <p class="trf-section-copy">Add optional context shown in trainer-facing experiences.</p>
            </div>

            <div class="trf-field-stack">
              <v-text-field
                class="trf-field trf-field--textarea"
                name="aboutMe"
                v-model="trainer.aboutMe"
                :block="true"
                :multiple-lines="true">
                <template slot="label">
                  About me
                </template>
              </v-text-field>

              <v-text-field
                class="trf-field"
                name="introductionUrl"
                @keyup.enter="handleSubmit"
                v-model="trainer.introductionUrl"
                data-vv-value-path="input" data-vv-name="introductionUrl"
                :block="true">
                <template slot="label">
                  Trainer introduction web
                </template>
              </v-text-field>
            </div>
          </section>

          <section class="trf-section">
            <div class="trf-section-heading">
              <span class="trf-section-kicker">Media</span>
              <p class="trf-section-copy">Upload the trainer photo used across console and mobile surfaces.</p>
            </div>

            <div class="trf-upload-row">
              <div class="trf-upload-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div class="trf-upload-body">
                <v-upload-field
                  class="trf-upload-field"
                  @fileUrl="setPhotoUrl"
                  url="trainers/upload-image"
                  :block="true">
                  <template slot="label">
                    Photo upload
                  </template>
                </v-upload-field>
                <p class="trf-upload-note">Use a clear trainer image. Uploaded files continue through the existing trainer image endpoint.</p>
                <div v-if="trainer.photoUrl" class="trf-upload-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Photo uploaded
                </div>
              </div>
            </div>
          </section>

          <div class="trf-actions">
            <v-button class="trf-save-btn" @click="handleSubmit" :disabled="loading">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              {{ loading ? 'Saving...' : 'Save Trainer' }}
            </v-button>
          </div>
        </v-form>
      </div>
    </container>
  </div>
</template>

<script>
import Container from '@/components/Container'
import VForm from '@/components/Forms/VForm'
import VTextField from '@/components/Forms/VTextField'
import VUploadField from '@/components/Forms/VUploadField'
import VButton from '@/components/Forms/VButton'
import { mapActions } from 'vuex'

export default {
  name: 'TrainerManager',
  data: () => ({
    trainer: {
      aboutMe: '',
      firstName: '',
      lastName: '',
      nickname: '',
      photoUrl: null,
      user: {
        firstName: '',
        lastName: '',
        activated: true,
        email: '',
        langKey: 'en',
        login: '',
        password: ''
      }
    },
    loading: false,
    error: ''
  }),
  methods: {
    async handleSubmit () {
      const isValid = await this.$validator.validateAll()

      if (isValid) {
        this.loading = true

        const isAdded = await this.addTrainer(this.trainer)

        this.loading = false

        if (isAdded) {
          this.$router.push('/admin/trainers')

          return true
        }

        this.error = 'Something wrong while trying to add the trainer'

        return false
      }
    },
    setPhotoUrl (value) {
      this.trainer.photoUrl = value
    },
    ...mapActions({
      addTrainer: 'trainers/addTrainer'
    })
  },
  watch: {
    'trainer.firstName' () {
      this.trainer.user.firstName = this.trainer.firstName
    },
    'trainer.lastName' () {
      this.trainer.user.lastName = this.trainer.lastName
    },
    'trainer.nickname' () {
      this.trainer.user.login = this.trainer.nickname
    }
  },
  components: {
    Container,
    VButton,
    VForm,
    VTextField,
    VUploadField
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.trf-page {
  color: #1a1a2e;
  font-family: 'Inter', sans-serif;
  padding: 0 0 30px;
}

.trf-card {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  padding: 1.5rem 1.75rem;
}

.trf-card-header,
.trf-upload-row,
.trf-actions,
.trf-error,
.trf-upload-success {
  display: flex;
  align-items: center;
}

.trf-card-header {
  gap: 12px;
  border-bottom: 1px solid #f0f0f5;
  padding-bottom: 1rem;
  margin-bottom: 1.35rem;
}

.trf-icon,
.trf-upload-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.trf-icon svg,
.trf-upload-icon svg,
.trf-error svg,
.trf-upload-success svg,
.trf-save-btn svg {
  width: 16px;
  height: 16px;
}

.trf-card-title {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.trf-card-subtitle,
.trf-section-copy,
.trf-upload-note {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 400;
  margin: 3px 0 0;
}

.trf-error {
  gap: 8px;
  background: #fff5f5;
  border: 1px solid #ffd2d2;
  border-radius: 9px;
  color: #c0392b;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 1.1rem;
  padding: 0.75rem 0.85rem;
}

.trf-section {
  border-bottom: 1px solid #f0f0f5;
  padding: 0 0 1.35rem;
  margin-bottom: 1.35rem;
}

.trf-section-heading {
  margin-bottom: 0.9rem;
}

.trf-section-kicker {
  color: #1a1a2e;
  display: block;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.trf-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.15rem 1rem;
}

.trf-field-stack {
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
}

.trf-field,
.trf-upload-field {
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 600;
  text-transform: none;
}

.trf-field .text-field,
.trf-upload-field .text-field {
  background: #fafafa;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  color: #1a1a2e;
  font-size: 13px;
  margin-top: 6px;
  min-height: 36px;
  padding: 9px 12px;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.trf-field .text-field:focus,
.trf-upload-field .text-field:focus {
  background: #fff;
  border-color: #5b4fcf;
  box-shadow: 0 0 0 3px rgba(91,79,207,0.12);
}

.trf-field--textarea .text-field {
  min-height: 110px;
}

.trf-field .error,
.trf-upload-field .error {
  color: #c0392b;
  font-size: 12px;
  top: calc(100% + 3px);
}

.trf-field.has-error,
.trf-upload-field.has-error {
  color: #c0392b;
}

.trf-field.has-error .text-field,
.trf-upload-field.has-error .text-field {
  border-color: #c0392b;
}

.trf-required {
  color: #c0392b;
  font-weight: 700;
}

.trf-upload-row {
  align-items: flex-start;
  gap: 12px;
  background: #fafafa;
  border: 1.5px dashed #d0d0e0;
  border-radius: 9px;
  padding: 1rem;
}

.trf-upload-body {
  flex: 1 1 auto;
  min-width: 0;
}

.trf-upload-note {
  line-height: 1.4;
}

.trf-upload-success {
  gap: 6px;
  color: #2f8f46;
  font-size: 12px;
  font-weight: 700;
  margin-top: 0.55rem;
}

.trf-actions {
  justify-content: flex-end;
}

.trf-save-btn.btn {
  background: #5b4fcf;
  border: none;
  border-radius: 8px;
  color: #fff;
  gap: 8px;
  height: 36px;
  line-height: 36px;
  padding: 0 18px;
  font-size: 13px;
  font-weight: 600;
  text-transform: none;
}

.trf-save-btn.btn:hover {
  background: #4338ca;
}

.trf-save-btn.btn--disabled {
  opacity: 0.65;
}

@media (max-width: 760px) {
  .trf-card {
    padding: 1.15rem;
  }

  .trf-card-header,
  .trf-upload-row {
    align-items: flex-start;
  }

  .trf-field-grid {
    grid-template-columns: 1fr;
  }

  .trf-actions {
    justify-content: stretch;
  }

  .trf-save-btn.btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// Add.vue?ae571508